import { Sankey, Tooltip, ResponsiveContainer, Rectangle, Layer } from 'recharts'
import AnalyticsEmptyState from '../../dashboard/AnalyticsEmptyState'

// Low → high ramp (coral → dark teal), brand colors. Color is reserved for
// CHANGE: a link is colored by its start bucket only when the level moved.
// Everything that stayed the same — node bars and "stayed" links — is grey.
const RAMP = ['#f38284', '#ef9f72', '#e2c062', '#7cbd9d', '#206c7f']
const STAYED_COLOR = '#cfcfcf'
const NODE_COLOR = '#757575'

/**
 * Maps a bucket value to a ramp color across the scale.
 * @param {number} bucket @param {number} lo @param {number} hi @returns {string}
 */
const bucketColor = (bucket, lo, hi) => {
  const span = hi - lo
  const idx = span > 0 ? Math.round(((bucket - lo) / span) * (RAMP.length - 1)) : 0
  return RAMP[Math.min(RAMP.length - 1, Math.max(0, idx))]
}

/**
 * Custom node: a bar + a compact label outside the flow. The label drops the
 * "Start"/"Stop" word (the left column is always the start, the right the stop)
 * and shows just "level: share", e.g. "4: 75%", so it fits a narrow card.
 * @param {object} props - injected by recharts Sankey (x, y, width, height, payload)
 */
const TransitionNode = ({ x, y, width, height, payload }) => {
  const isStart = payload.side === 'start'
  const textX = isStart ? x - 6 : x + width + 6
  const anchor = isStart ? 'end' : 'start'
  return (
    <Layer>
      <Rectangle x={x} y={y} width={width} height={height} fill={NODE_COLOR} fillOpacity={0.9} />
      <text
        className='matrix-transition-sankey-node-label'
        x={textX}
        y={y + height / 2}
        textAnchor={anchor}
        dominantBaseline='middle'
        fill='#333333'
      >
        {payload.bucket}: {payload.percent}%
      </text>
    </Layer>
  )
}

/**
 * Custom link: grey when the answer stayed on the same bucket, otherwise colored
 * by the start bucket.
 * @param {object} props - injected by recharts Sankey
 */
const TransitionLink = ({ sourceX, sourceY, sourceControlX, targetControlX, targetX, targetY, linkWidth, payload, lowerScale, upperScale }) => {
  if (payload.anchor) return null // invisible placeholder that anchors empty levels
  const color = payload.stayed ? STAYED_COLOR : bucketColor(payload.startBucket, lowerScale, upperScale)
  return (
    <path
      d={`M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
      fill='none'
      stroke={color}
      strokeWidth={linkWidth}
      strokeOpacity={payload.stayed ? 0.45 : 0.75}
    />
  )
}

/**
 * Transition Sankey for an MSI effect: shows how respondents moved across the
 * 1–5 scale from the first to the last measure moment (persona-paired, pooled
 * across rows). Renders nothing useful without paired data — the picker only
 * offers this view when `hasPersonaTransitions` is true.
 * AI-CONTEXT: See docs/features/msi-transition-sankey.md
 *
 * The benchmark growth shows as a caption (it has no per-bucket distribution).
 *
 * @param {object} props
 * @param {{mode: string, nodes: Array, links: Array}} props.transitionSankey - from useMatrixData
 * @param {number} props.lowerScale - Scale lower bound (for link colors)
 * @param {number} props.upperScale - Scale upper bound (for link colors)
 * @param {number|null} [props.benchmarkGrowth] - benchmark growth % (caption only)
 */
const MatrixTransitionSankey = ({ transitionSankey, lowerScale, upperScale, benchmarkGrowth }) => {
  if (!transitionSankey || transitionSankey.mode === 'empty' || !transitionSankey.links?.length) {
    return <AnalyticsEmptyState />
  }

  const nodes = transitionSankey.nodes

  const hasBenchmark = typeof benchmarkGrowth === 'number' && Number.isFinite(benchmarkGrowth)

  return (
    <div className='matrix-transition-sankey-container'>
      <div className='matrix-transition-sankey-headers'>
        <span>Voor</span>
        <span>Na</span>
      </div>
      <ResponsiveContainer width='100%' height={Math.max(280, nodes.length * 36)}>
        <Sankey
          data={{ nodes, links: transitionSankey.links }}
          nodeWidth={10}
          nodePadding={30}
          iterations={0}
          margin={{ top: 20, right: 60, bottom: 20, left: 60 }}
          node={<TransitionNode />}
          link={<TransitionLink lowerScale={Number(lowerScale)} upperScale={Number(upperScale)} />}
        >
          <Tooltip />
        </Sankey>
      </ResponsiveContainer>
      {hasBenchmark && (
        <p className='matrix-transition-sankey-caption'>
          Benchmark groei {benchmarkGrowth >= 0 ? '+' : ''}{benchmarkGrowth}%
        </p>
      )}
    </div>
  )
}

export default MatrixTransitionSankey
